import React, { useEffect, useState } from 'react';
import { X, Shield, Check, Loader2 } from 'lucide-react';
import { adminAPI, jobsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * Permissions Manager modal (Access Model V2).
 * Opened from User Management → "Manage Permissions" per user.
 *
 * Renders:
 *   • Super admin toggle
 *   • Grouped menu checklist (allowed_menus)
 *   • Per-project CRUD grid (projects[].can_view/create/edit/delete)
 */
const PermissionsManagerModal = ({ user, isOpen, onClose, onSaved }) => {
  const { user: currentUser, refreshUser } = useAuth();
  const [menus, setMenus] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState(new Set());
  const [projectPerms, setProjectPerms] = useState({}); // { project_id: {can_view, ...} }

  useEffect(() => {
    if (!isOpen || !user) return;
    setLoading(true);
    Promise.all([adminAPI.listMenus(), jobsAPI.getAll()])
      .then(([menuRes, jobRes]) => {
        setMenus(menuRes.data || []);
        setProjects(jobRes.data || []);

        setIsSuperAdmin(!!user.is_super_admin);
        setAllowedMenus(new Set(user.permissions?.allowed_menus || []));
        const map = {};
        (user.permissions?.projects || []).forEach(p => { map[p.project_id] = p; });
        setProjectPerms(map);
      })
      .catch(() => toast.error('Failed to load permissions data'))
      .finally(() => setLoading(false));
  }, [isOpen, user]);

  if (!isOpen) return null;

  const toggleMenu = (slug) => {
    setAllowedMenus(prev => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  };

  const toggleProjectPerm = (projectId, action) => {
    setProjectPerms(prev => {
      const current = prev[projectId] || { project_id: projectId, can_view: false, can_create: false, can_edit: false, can_delete: false };
      return { ...prev, [projectId]: { ...current, project_id: projectId, [action]: !current[action] } };
    });
  };

  const setAllForProject = (projectId, val) => {
    setProjectPerms(prev => ({
      ...prev,
      [projectId]: { project_id: projectId, can_view: val, can_create: val, can_edit: val, can_delete: val },
    }));
  };

  const grantAllMenus = () => setAllowedMenus(new Set(menus.map(m => m.slug)));
  const clearAllMenus = () => setAllowedMenus(new Set());

  const groups = Array.from(new Set(menus.map(m => m.group))).sort();

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.updateUserPermissions(user.id, {
        is_super_admin: isSuperAdmin,
        allowed_menus: Array.from(allowedMenus),
        projects: Object.values(projectPerms).filter(p =>
          p.can_view || p.can_create || p.can_edit || p.can_delete
        ),
      });
      toast.success('Permissions saved');
      // If admin edited their OWN permissions, refresh the AuthContext so the sidebar
      // immediately reflects the new access set (no reload needed).
      if (currentUser && user && currentUser.id === user.id) {
        await refreshUser();
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="permissions-modal">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Manage Permissions</h3>
              <p className="text-sm text-slate-500">{user?.name} • {user?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="permissions-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading permissions…
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Super admin toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50">
              <div>
                <p className="text-sm font-semibold text-amber-900">Super Admin</p>
                <p className="text-xs text-amber-700">Bypasses all menu and project restrictions. Grant sparingly.</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSuperAdmin}
                  onChange={(e) => setIsSuperAdmin(e.target.checked)}
                  className="sr-only peer"
                  data-testid="super-admin-toggle"
                />
                <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-amber-500 relative transition after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:w-5 after:h-5 after:transition peer-checked:after:translate-x-5" />
              </label>
            </div>

            {/* Menus */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-800">Menu Access ({allowedMenus.size}/{menus.length})</h4>
                <div className="flex gap-2">
                  <button onClick={grantAllMenus} className="text-xs text-blue-600 hover:underline" data-testid="grant-all-menus">Grant all</button>
                  <span className="text-slate-300">•</span>
                  <button onClick={clearAllMenus} className="text-xs text-slate-500 hover:underline" data-testid="clear-all-menus">Clear all</button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {groups.map(group => (
                  <div key={group} className="rounded-lg border border-slate-200 p-3">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{group}</p>
                    <div className="space-y-1.5">
                      {menus.filter(m => m.group === group).map(m => (
                        <label key={m.slug} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={allowedMenus.has(m.slug)}
                            onChange={() => toggleMenu(m.slug)}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            data-testid={`menu-${m.slug}`}
                          />
                          {m.name}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Projects CRUD */}
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-2">Project (Job) Access</h4>
              <p className="text-xs text-slate-500 mb-3">Grant per-project CRUD. Projects with no permissions are hidden from this user.</p>
              {projects.length === 0 ? (
                <p className="text-sm text-slate-500">No active projects.</p>
              ) : (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm" data-testid="projects-grid">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left p-2">Project</th>
                        <th className="p-2 text-center">View</th>
                        <th className="p-2 text-center">Create</th>
                        <th className="p-2 text-center">Edit</th>
                        <th className="p-2 text-center">Delete</th>
                        <th className="p-2 text-center">All</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map(p => {
                        const perms = projectPerms[p.id] || {};
                        const cell = (action) => (
                          <td className="text-center p-1">
                            <input
                              type="checkbox"
                              checked={!!perms[action]}
                              onChange={() => toggleProjectPerm(p.id, action)}
                              className="w-4 h-4"
                              data-testid={`project-${p.id}-${action}`}
                            />
                          </td>
                        );
                        const allOn = perms.can_view && perms.can_create && perms.can_edit && perms.can_delete;
                        return (
                          <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="p-2">
                              <p className="font-medium text-slate-800">{p.title || p.job_code}</p>
                              <p className="text-[11px] text-slate-500">{p.job_code} • {p.client_name}</p>
                            </td>
                            {cell('can_view')}
                            {cell('can_create')}
                            {cell('can_edit')}
                            {cell('can_delete')}
                            <td className="text-center p-1">
                              <button
                                onClick={() => setAllForProject(p.id, !allOn)}
                                className={`text-xs px-2 py-0.5 rounded ${allOn ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'} hover:opacity-80`}
                                data-testid={`project-${p.id}-toggle-all`}
                              >
                                {allOn ? <Check className="w-3 h-3 inline" /> : 'Grant'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100" data-testid="permissions-cancel">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm disabled:opacity-60"
            data-testid="permissions-save"
          >
            {saving ? 'Saving…' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionsManagerModal;
